import { createFileRoute } from "@tanstack/react-router";
import {
  Container,
  CtaBand,
  Eyebrow,
  Lead,
  PrimaryLink,
  Quote,
  TextLink,
} from "@/components/site/primitives";
import { SITE, TESTIMONIALS } from "@/lib/site-content";
import { ogImageMeta } from "@/lib/og";
import { PartOfHub, RelatedLinks } from "@/components/site/RelatedLinks";
import { breadcrumbMeta } from "@/lib/internal-links";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { contactFunnel, trackOutboundClick } from "@/lib/analytics";
import { AREAS, jsonLdScript, professionalServiceSchema, serviceSchema } from "@/lib/schema";

const PATH = "/fractional-cto-kuala-lumpur";
const CTA_SOURCE_HERO = "fractional_cto_kuala_lumpur_hero";
const CTA_SOURCE_BAND = "fractional_cto_kuala_lumpur_footer";

const title = "Fractional CTO in Kuala Lumpur | Timothy Ng, Scryla";
const description =
  "Delivery behind plan and no senior technologist owning it? Fractional CTO for Kuala Lumpur founders and boards, working remote-first on MYT (GMT+8).";

const NEED = [
  {
    title: "Senior judgement, part-time",
    body: "Architecture, hiring, spend and security decisions are executive-level long before the headcount can carry a full-time CTO. Fractional covers the judgement at a cadence a KL-stage business can actually fund.",
  },
  {
    title: "Accountability for the build",
    body: "Whether the team is in-house in KL or outsourced across the region, someone independent has to read what was promised against what shipped, and say so in writing.",
  },
];

const WORKING = [
  {
    title: "Same working day, not the next one",
    body: "MYT is GMT+8 year-round, so a 09:00 UK start reaches you at 16:00 in UK summer and 17:00 in winter. A KL team gets an answer inside its own working day rather than reading it the next morning.",
  },
  {
    title: "Remote by default",
    body: "Fixed weekly contact, board attendance by video, and written reports founders and investors can both act on. That is how the engagement runs unless you need otherwise.",
  },
  {
    title: "On-site in KL by arrangement",
    body: "A board offsite, a team reset or a diligence week is worth being in the room for. Travel and on-site days are agreed and costed as part of the engagement rather than assumed.",
  },
];

const FAQ = [
  {
    q: "Are you based in Kuala Lumpur?",
    a: `No. I am based in ${SITE.location}. Work with KL clients runs remote-first on your hours. I would rather be straight about that than dress a service area up as a local office.`,
  },
  {
    q: "How does the time difference work with the UK?",
    a: "Malaysia stays on GMT+8 all year and the UK moves, so the gap is seven hours from late March to late October and eight hours otherwise. The practical effect for a Klang Valley team is a standing late-afternoon slot: escalations, stand-up attendance and board calls land before your day ends, and anything outside it comes back in writing overnight.",
  },
  {
    q: "Do you travel to Malaysia?",
    a: "Yes, where being in the room changes the outcome. On-site time is arranged and costed per engagement so the cost is visible before you commit, not added afterwards.",
  },
  {
    q: "What does a fractional CTO actually do?",
    a: "Audits where your technology, team, spend and risk stand today; sets a technology plan that follows from the business plan, with sequence and cost named; holds in-house and outsourced development teams to what they promised; and coaches founders and technical leads so the capability stays in the business.",
  },
  {
    q: "What does it cost?",
    a: "Agreed per engagement, not published. For Malaysian clients the two things that change the shape are cadence and whether any time on the ground is needed, and travel is costed separately and openly rather than buried in a rate.",
  },
];

export const Route = createFileRoute("/fractional-cto-kuala-lumpur")({
  // Static marketing content: let the CDN serve it and revalidate in the background.
  headers: () => ({
    "cache-control": "public, max-age=0, s-maxage=3600, stale-while-revalidate=86400",
  }),
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE.url}${PATH}` },
      ...ogImageMeta(
        "fractional-cto-kuala-lumpur",
        "Fractional CTO in Kuala Lumpur, GMT+8 aligned | Timothy Ng",
      ),
    ],
    links: [{ rel: "canonical", href: `${SITE.url}${PATH}` }],
    scripts: [
      jsonLdScript(
        professionalServiceSchema({
          path: PATH,
          name: "Fractional CTO in Kuala Lumpur | Timothy Ng, Scryla",
          areaServed: AREAS.kualaLumpur,
          serviceType: "Fractional CTO",
        }),
      ),
      jsonLdScript(
        serviceSchema({
          path: PATH,
          serviceType: "Fractional CTO",
          areaServed: AREAS.kualaLumpur,
        }),
      ),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: { "@type": "Answer", text: item.a },
          })),
        }),
      },
      breadcrumbMeta(PATH),
    ],
  }),
  component: KualaLumpurPage,
});

function KualaLumpurPage() {
  return (
    <div>
      <section
        aria-labelledby="fctokl-hero-heading"
        className="border-b border-border bg-gradient-to-b from-background to-surface"
      >
        <Container>
          <Breadcrumbs path={PATH} className="mb-6" />
          <Eyebrow>Fractional CTO · Kuala Lumpur</Eyebrow>
          <h1
            id="fctokl-hero-heading"
            className="mt-4 max-w-[22ch] text-[34px] leading-[1.06] font-bold tracking-[-0.035em] text-balance md:text-5xl"
          >
            Fractional CTO in Kuala Lumpur.
          </h1>
          <Lead className="mt-6">
            Delivery slower than the plan and no executive technologist to call it? Technical
            leadership for KL founders and boards who need executive judgement now and a full-time
            CTO later. Former CTO and COO, and founder of my own consultancy since 2014.
          </Lead>
          <PartOfHub hub="/fractional-cto-malaysia" className="mt-5" />
          <div className="mt-8 flex flex-wrap gap-3">
            <PrimaryLink to="/contact" onClick={() => contactFunnel.ctaClick(CTA_SOURCE_HERO)}>
              Arrange a conversation
            </PrimaryLink>
            <a
              href={SITE.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackOutboundClick("linkedin", CTA_SOURCE_HERO)}
              className="inline-flex items-center gap-2 rounded-lg border border-border-strong bg-background px-6 py-3.5 text-[15px] font-semibold transition-colors hover:border-primary hover:text-primary"
            >
              View my LinkedIn profile
            </a>
          </div>
        </Container>
      </section>

      <section className="border-b border-border" aria-labelledby="fctokl-need-heading">
        <Container>
          <Eyebrow>Where I fit</Eyebrow>
          <h2
            id="fctokl-need-heading"
            className="mt-3 max-w-[24ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            Two things founders here ask for
          </h2>
          <ul className="mt-8 grid gap-5 md:grid-cols-2">
            {NEED.map((item) => (
              <li key={item.title} className="rounded-lg border border-border bg-card p-6">
                <h3 className="text-lg font-semibold">{item.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-body">{item.body}</p>
              </li>
            ))}
          </ul>
          <RelatedLinks className="mt-7" paths={["/fractional-cto-vs-full-time-vs-agency"]} />
        </Container>
      </section>

      <section className="border-b border-border bg-surface" aria-labelledby="fctokl-work-heading">
        <Container>
          <Eyebrow>How the engagement works</Eyebrow>
          <h2
            id="fctokl-work-heading"
            className="mt-3 max-w-[26ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            Delivery cadence for KL clients
          </h2>
          <ul className="mt-8 grid gap-5 md:grid-cols-3">
            {WORKING.map((item) => (
              <li key={item.title} className="rounded-lg border border-border bg-card p-6">
                <h3 className="text-lg font-semibold">{item.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-body">{item.body}</p>
              </li>
            ))}
          </ul>
          <p className="mt-7 max-w-[62ch] text-[15px] leading-relaxed text-body">
            Sectors I have run technology and operations in: fintech, iGaming and gambling, digital
            health, SaaS, data and agencies. I take a small number of clients at a time, with no
            development team to keep busy and no supplier referrals to protect.
          </p>
        </Container>
      </section>

      <section className="border-b border-border" aria-labelledby="fctokl-quotes-heading">
        <Container>
          <h2 id="fctokl-quotes-heading" className="sr-only">
            What founders say
          </h2>
          <div className="grid gap-5 md:grid-cols-2">
            <Quote item={TESTIMONIALS.milanShah} />
            <Quote item={TESTIMONIALS.danielReilly} />
          </div>
        </Container>
      </section>

      <section className="border-b border-border bg-surface" aria-labelledby="fctokl-faq-heading">
        <Container>
          <Eyebrow>Questions</Eyebrow>
          <h2
            id="fctokl-faq-heading"
            className="mt-3 text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            Kuala Lumpur FAQs
          </h2>
          <dl className="mt-8 divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
            {FAQ.map((item) => (
              <div key={item.q} className="px-6 py-6">
                <dt className="text-lg font-semibold text-ink">{item.q}</dt>
                <dd className="mt-2 max-w-[70ch] text-[15px] leading-relaxed text-body">
                  {item.a}
                </dd>
              </div>
            ))}
          </dl>
          <RelatedLinks
            className="mt-7"
            paths={["/fractional-cto-malaysia", "/fractional-cto-southeast-asia"]}
          />
        </Container>
      </section>

      <CtaBand
        heading="Building in Kuala Lumpur?"
        body="Tell me about the business, the stage it is at, and what is worrying you. I will tell you plainly whether I am the right person, and what I would look at first."
        to="/contact"
        cta="Arrange a conversation"
        onCtaClick={() => contactFunnel.ctaClick(CTA_SOURCE_BAND)}
      />
    </div>
  );
}
